import { computed, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useFirebaseStore, type flightEntity } from 'freedom-shared';
import { dictEntity } from 'shared';
import type { Dict } from '@aviata/platform-types';
import dayjs from 'dayjs';

interface DetailsWarningBannerConfig {
  enabled: boolean;
  country_codes: string[];
  show_until: string;
  description_ru: string;
  description_kk: string;
  description_en: string;
}

type Locale = 'ru' | 'kk' | 'en';

function collectAirportCodes(offer: flightEntity.types.MappedOffer): string[] {
  const codes: string[] = [];

  for (const flight of offer.flights) {
    for (const segment of flight.segments) {
      if (segment.dep?.airport) codes.push(segment.dep.airport);
      if (segment.arr?.airport) codes.push(segment.arr.airport);

      if (segment.stops?.locations) {
        for (const location of segment.stops.locations) {
          if (location.airport) codes.push(location.airport);
        }
      }
    }
  }

  return codes;
}

function hasMatchingCountry(
  airportCodes: string[],
  countryCodes: Set<string>,
  dict: Dict.Entity,
): boolean {
  return airportCodes.some(iata => {
    const city = dictEntity.lib.getCityByAirport(iata, dict as never);
    return city?.country?.iso_code && countryCodes.has(city.country.iso_code);
  });
}

export function useDetailsWarningBanner(
  offer: Ref<flightEntity.types.MappedOffer | undefined>,
  dict: Dict.Entity,
) {
  const { locale } = useI18n();
  const { serializedRemoteConfig } = storeToRefs(useFirebaseStore());

  const config = computed<DetailsWarningBannerConfig | null>(
    () =>
      (serializedRemoteConfig.value
        ?.avfree_details_warning_banner as unknown as DetailsWarningBannerConfig) ??
      null,
  );

  const isVisible = computed(() => {
    if (!config.value?.enabled || !offer.value) return false;

    if (
      config.value.show_until &&
      dayjs().isAfter(dayjs(config.value.show_until), 'day')
    ) {
      return false;
    }

    const countryCodes = new Set(config.value.country_codes);
    const airportCodes = collectAirportCodes(offer.value);

    return hasMatchingCountry(airportCodes, countryCodes, dict);
  });

  const description = computed(() => {
    if (!config.value) return '';
    const lang = locale.value as Locale;
    return (
      config.value[`description_${lang}`] || config.value.description_ru || ''
    );
  });

  return { isVisible, description };
}
